import { amorphicStatic } from '@haventech/amorphic';
import { Component, OnInit, ViewChild } from '@angular/core';
import { AmorphicService } from '../../../services/amorphic/amorphic.service';
import { Customer } from '../../../../../common/js/customer/Customer';
import type { Application } from '../../../../../common/js/customer/Application';
import { Utils } from '../../../../../common/js/Utils';
import { AccountCenterService } from '../../../services/account-center/account-center.service';
import type { CoverpathAnnuityApplication } from '../../../../../coverpath-common/classes/coverpath_subclasses/CoverpathAnnuityApplication';
import { UICategory, UILoggerSingleton } from '../../../../../common/js/logger/UILoggerSingleton';
import { Controller } from 'apps/hit-snapshot-service/js/Controller';
import { ToasterComponent, ToasterIntent } from '../../overlays/toaster/toaster.component';

export type PoliciesOverviewTabName = 'Pending' | 'Active' | 'Canceled';

const NB_DECOMMISSION_BANNER_TEXT =
  'Coverpath is no longer accepting new applications. Please speak with your financial professional to submit a new application.';

@Component({
  moduleId: module.id,
  selector: 'policies-overview',
  templateUrl: './policies-overview.component.html',
})
export class PoliciesOverviewComponent implements OnInit {
  customer: Customer;
  tabNameToApplications: { [key in PoliciesOverviewTabName]: Application[] };
  selectedTab: PoliciesOverviewTabName;
  nbDecommissionPost30Days: boolean = false;
  @ViewChild('toasterInstance') toasterInstance: ToasterComponent;
  constructor(private amorphicService: AmorphicService, protected accountCenterService: AccountCenterService) {
    this.nbDecommissionPost30Days = this.amorphicService.controller.nbDecommissionPost30Days;
  }

  async ngOnInit() {
    await this.accountCenterService.setPolicyViewDataForListView();
    await this.amorphicService.controller.publicSave(); // this is to get the most up to date info, particlarly settings is the preServerCall hook
    this.customer = this.amorphicService.controller.customer;
    this.tabNameToApplications = this.bucketApplicationsByTab(this.customer.activeApplications);
    this.selectedTab = this.getSelectedTab(this.tabNameToApplications);
  }

  async ngAfterViewInit() {
    if (this.nbDecommissionPost30Days) {
      this.toasterInstance.notify(NB_DECOMMISSION_BANNER_TEXT, { intent: ToasterIntent.DECOMMISSION });
    }
  }

  private bucketApplicationsByTab(applications: Application[]): { [key in PoliciesOverviewTabName]: Application[] } {
    const bucketedApplications = {
      Pending: [],
      Active: [],
      Canceled: [],
    };

    applications.forEach((application) => {
      if (application.primaryPolicy.isInForce()) {
        bucketedApplications.Active.push(application);
      } else if (application.primaryPolicy.wasCanceledDeclinedRejected()) {
        bucketedApplications.Canceled.push(application);
      } else if (application.primaryPolicy.convertedFrom) {
        if (this.amorphicService.controller.enableConversionAutomation) {
          bucketedApplications.Pending.push(application);
        } else {
          return;
        }
      } else if (
        application.primaryPolicy.convertedFrom &&
        !this.amorphicService.controller.enableConversionAutomation
      ) {
      } else if (application.isAgentAssistant && this.getApplicationLinkHasBeenSent(application)) {
        bucketedApplications.Pending.push(application);
      } else if (!application.isAgentAssistant) {
        bucketedApplications.Pending.push(application);
      } else {
        const error = {
          isHumanRelated: false,
          data: Utils.extractIds(application),
        };
        UILoggerSingleton.getInstance().error({
          module: 'PoliciesOverviewComponent',
          function: 'bucketApplicationsByTab',
          category: UICategory.request,
          error: error,
        });
      }
    });

    return bucketedApplications;
  }

  private getSelectedTab(tabNameToApplications: {
    [key in PoliciesOverviewTabName]: Application[];
  }): PoliciesOverviewTabName {
    if (tabNameToApplications.Pending.length > 0) {
      return 'Pending';
    } else if (tabNameToApplications.Active.length > 0) {
      return 'Active';
    } else {
      return 'Canceled';
    }
  }

  private getApplicationLinkHasBeenSent(application: Application) {
    return Boolean(application.primaryPolicy.linkSentDate);
  }

  asAnnuityApp(application: Application): CoverpathAnnuityApplication {
    return application as CoverpathAnnuityApplication;
  }
}
